import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import UserService from '../../services/userService';
import { useSupabaseAuthClient } from '../../hooks/useSupabaseAuthClient';
import AuthHeader from '../AuthHeader';
import Footer from '../Footer';
import './Signup.css';
import supabase from '../../services/supabaseClient';
import { useTranslation } from '../../i18n';

function Signup({ navigate }) {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const [validationErrors, setValidationErrors] = useState({});
  const [registeredEmail, setRegisteredEmail] = useState('');
  const client = useSupabaseAuthClient();

  const handleEmailChange = (event) => {
    event.preventDefault();
    setEmail(event.target.value);
  };

  const handlePasswordChange = (event) => {
    event.preventDefault();
    setPassword(event.target.value);
  };

  const validateForm = () => {
    const errors = {};
    
    if (!email.trim()) {
      errors.email = t('login.emailRequired');
    } else if (!/\S+@\S+\.\S+/.test(email)) {
      errors.email = t('login.emailInvalid');
    }

    if (!password) {
      errors.password = t('login.passwordRequired');
    } else if (password.length < 7) {
      errors.password = t('signup.passwordMin');
    } else if (!/[a-z]/.test(password)) {
      errors.password = t('signup.passwordLower');
    } else if (!/[0-9]/.test(password)) {
      errors.password = t('signup.passwordNum');
    }
    
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleReg = async (event) => {
    event.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setSending(true);
    setError('');

    try {
      const { user, session } = await UserService.signupWithClient(client, {
        email: email,
        password: password,
      });

      // Supabase returns a user with no identities when the email is already registered
      if (user && user.identities && user.identities.length === 0) {
        throw new Error(t('signup.emailExists'));
      }

      setSending(false);

      if (session) {
        // Email confirmation is disabled - the user is already signed in
        navigate('/');
      } else {
        // Confirmation email sent - show the "check your email" screen
        setRegisteredEmail(email);
      }
    } catch (error) {
      handleError(error);
    }
  };

  const handleError = (error) => {
    setSending(false);
    setError(error ? error.message : t('login.unexpectedError'));
  };

  const handleGoogleSignUp = async (event) => {
    event.preventDefault();
    setSending(true);
    setError('');
    try {
      const { data, error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: `${window.location.origin}/auth/callback`
        }
      });
      
      if (error) {
        throw error;
      }
      // User will be redirected to Google, no need to setSending(false) here
    } catch (error) {
      setSending(false);
      handleError(error);
    }
  };

  if (registeredEmail) {
    return (
      <div className="auth-page-container">
        <div className="auth-content">
          <div className="container">
            <AuthHeader />
            <div className="auth-form-container">
              <div className="form-signin text-center">
                <h1 className="h3 mb-3 font-weight-normal">{t('signup.checkEmail')}</h1>
                <div className="form-box">
                  <div className="alert alert-success" role="alert">
                    <i className="fa fa-envelope mr-2" aria-hidden="true"></i>
                    {t('signup.sentConfirmationBefore')} <strong>{registeredEmail}</strong>.
                  </div>
                  <p className="text-muted">
                    {t('signup.activateInfo')}
                  </p>
                  <hr className="mt-3 mb-3" />
                  <a href="/login" className="btn btn-outline-primary w-100">{t('signup.goToLogin')}</a>
                </div>
              </div>
            </div>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="auth-page-container">
      <div className="auth-content">
        <div className="container">
          <AuthHeader />
          <div className="auth-form-container">
            <form className="form-signin">
              <h1 className="h3 mb-3 font-weight-normal text-center">{t('signup.title')}</h1>

              <div className="form-box">
                <div className="form-group">
                  <label htmlFor="inputEmail">{t('login.email')}</label>
                  <input
                    type="email"
                    required=""
                    className={`form-control ${validationErrors.email ? 'is-invalid' : ''}`}
                    id="inputEmail"
                    value={email}
                    onChange={handleEmailChange}
                    disabled={sending}
                  />
                  {validationErrors.email && (
                    <div className="invalid-feedback d-block">{validationErrors.email}</div>
                  )}
                  <small className="form-text text-muted">{t('signup.emailNote')}
                  </small>
                </div>

                <div className="form-group">
                  <label htmlFor="password">{t('login.password')}</label>
                  <input
                    type="password"
                    required=""
                    className={`form-control ${validationErrors.password ? 'is-invalid' : ''}`}
                    id="password"
                    value={password}
                    onChange={handlePasswordChange}
                    disabled={sending}
                  />
                  {validationErrors.password && (
                    <div className="invalid-feedback d-block">{validationErrors.password}</div>
                  )}
                  <small className="form-text text-muted">{t('signup.passwordHint')}
                  </small>
                </div>

                <hr className="mt-3 mb-3" />
                <p className="privacy">{t('signup.agreePrefix')} <a href="/terms">{t('signup.termsLink')}</a>  <a href="/privacy">{t('signup.privacyLink')}</a></p>
                <hr className="mt-3 mb-3" />
                {error ? (<div className="alert alert-danger" role="alert">
                  {error}
                </div>) : null}

                {sending ?
                  (<div className="progress">
                    <div className="progress-bar w-100 progress-bar-striped progress-bar-animated" role="progressbar" aria-valuenow="75" aria-valuemin="0" aria-valuemax="100" />
                  </div>)
                  :
                  (
                    <>
                      <button className="btn btn-bs btn-primary w-100 mb-3" type="submit" onClick={handleReg}>{t('signup.createAccount')}</button>
                      <div className="text-center mb-2">
                        <span className="text-muted">{t('login.or')}</span>
                      </div>
                      <button 
                        className="btn btn-outline-secondary w-100" 
                        type="button" 
                        onClick={handleGoogleSignUp}
                        disabled={sending}
                      >
                        <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          <svg className="mr-2" width="18" height="18" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 48 48">
                            <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
                            <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
                            <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
                            <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
                            <path fill="none" d="M0 0h48v48H0z"/>
                          </svg>
                          <span>{t('signup.signUpWithGoogle')}</span>
                        </span>
                      </button>
                    </>
                  )
                }
              </div>
            </form>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
}

const SignupWithNavigate = (props) => {
  const navigate = useNavigate();
  return <Signup {...props} navigate={navigate} />;
};

export default SignupWithNavigate;
